// @file <pdlt-mention> custom HTML5 element. Used to render a mention on Padlet.
import type { Mentioner, UserMentionSuggestionAttributes } from '../types'
import { __ } from './intl'

const MENTION_ELEMENT_TAG_NAME = 'pdlt-mention'

// Conservative allowlist for usernames. Only letters, digits and underscores are allowed, so a username
// matching this pattern can never break out of an HTML attribute, URL path, or text context and is always
// safe to render. This is kept in sync with the canonical client-side `USERNAME_REGEX` /
// `MAX_NAMESPACE_LENGTH` (services/rails/app/javascript/bits/{regex,numbers}.ts), which in turn mirror the
// server-side username format (VALID_FORMAT in app/models/namespace.rb). The `@@` Rails alias is not
// importable from this standalone package, so the canonical pattern is copied here verbatim.
const MAX_NAMESPACE_LENGTH = 64
const VALID_USERNAME_REGEX = new RegExp(`^(?!.*[_]{2}.*)[a-zA-Z0-9]{1}[a-zA-Z0-9_]{0,${MAX_NAMESPACE_LENGTH - 1}}$`)
const SUPPORTED_MENTION_TYPES = ['user'] as const
type MentionType = (typeof SUPPORTED_MENTION_TYPES)[number]
type Mentionee = Pick<UserMentionSuggestionAttributes, 'hashid' | 'username' | 'display_name'>

/**
 * A custom HTML5 element used to render a mention on Padlet.
 * If the type is "user", the element renders an anchor tag that links to the mentioned user's profile.
 *
 * @example
 * <pdlt-mention
 *   type="user"
 *   mentioner-id="yLA6m0oM"
 *   mentionee-id="bUYIm767"
 *   mentionee-username="pizza"
 * >
 *   Pizza Boy
 * </pdlt-mention>
 *
 * @param {string} type - The type of mention. Currently only `user` is supported.
 * @param {string} mentioner-id - The public ID of the user who is mentioning someone.
 * @param {string} mentionee-id - The public ID of the user who is being mentioned.
 * @param {string} mentionee-username - The username of the user who is being mentioned.
 */
class PdltMention extends HTMLElement {
  /**
   * Create a new PdltMention element from a set of attributes.
   */
  static fromAttributes({
    type,
    mentioner,
    mentionee,
  }: {
    type: MentionType
    mentioner: Mentioner
    mentionee: Mentionee
  }): PdltMention {
    // We must register this custom element before we can create a new instance of it.
    // Otherwise, we will get 'Illegal constructor' error. See https://stackoverflow.com/a/61883392/8943850.
    if (window.customElements.get(MENTION_ELEMENT_TAG_NAME) == null) throw Error('PdltMention is not registered yet')

    const pdltMention = new PdltMention()
    pdltMention.setAttribute('type', type)
    pdltMention.setAttribute('mentioner-id', mentioner.hashid)
    pdltMention.setAttribute('mentionee-id', mentionee.hashid)
    pdltMention.setAttribute('mentionee-username', mentionee.username)
    pdltMention.textContent = mentionee.display_name
    return pdltMention
  }

  /**
   * Create a new PdltMention element from an HTML string.
   */
  static fromString(html: string): PdltMention | null {
    // We must register this custom element before we can create a new instance of it.
    // Otherwise, we will get 'Illegal constructor' error. See https://stackoverflow.com/a/61883392/8943850.
    if (window.customElements.get(MENTION_ELEMENT_TAG_NAME) == null) throw Error('PdltMention is not registered yet')

    const template = document.createElement('template')
    template.innerHTML = html

    // The browser can correct invalid HTML (e.g. auto close tags).
    // When it happens, the innerHTML will be updated after being set.
    // We'll be strict here and reject the HTML if it needs to be corrected.
    if (template.innerHTML !== html) {
      return null
    }

    const element = template.content.firstElementChild
    if (element === null) return null
    const interestedAttributes = {
      type: element.getAttribute('type'),
      'mentioner-id': element.getAttribute('mentioner-id'),
      'mentionee-id': element.getAttribute('mentionee-id'),
      'mentionee-username': element.getAttribute('mentionee-username'),
      mentioneeDisplayName: element.textContent,
    }

    // Reject mentions whose username is not in the allowlist. The username is later interpolated into a URL and
    // an anchor attribute, so refusing anything outside the safe character set stops malformed/malicious input
    // at the deserialization boundary.
    const username = interestedAttributes['mentionee-username']
    if (username !== null && !VALID_USERNAME_REGEX.test(username)) return null
    const pdltMention = new PdltMention()
    Object.entries(interestedAttributes).forEach(([key, value]) => {
      if (key === 'mentioneeDisplayName') {
        if (value !== null) pdltMention.textContent = value
      } else if (value !== null) {
        pdltMention.setAttribute(key, value)
      }
    })
    return pdltMention
  }

  get isValid(): boolean {
    if (this.type === 'user') {
      return (
        this.mentionerId !== null &&
        this.mentioneeId !== null &&
        this.mentioneeUsername !== null &&
        this.mentioneeDisplayName !== ''
      )
    }
    return false
  }

  get type(): MentionType | null {
    const rawType = this.getAttribute('type')
    if (rawType === null) return null
    const castedType = rawType as MentionType
    if (SUPPORTED_MENTION_TYPES.includes(castedType)) return castedType
    return null
  }

  get mentionerId(): string | null {
    return this.getAttribute('mentioner-id')
  }

  get mentioneeId(): string | null {
    return this.getAttribute('mentionee-id')
  }

  get mentioneeUsername(): string | null {
    return this.getAttribute('mentionee-username')
  }

  get mentioneeDisplayName(): string | null {
    return this.textContent
  }

  toString(): string {
    return this.outerHTML
  }

  connectedCallback(): void {
    if (this.type === 'user') {
      this.renderProfileLink()
    }
  }

  private renderProfileLink(): void {
    const username = this.mentioneeUsername
    if (username == null || username === '') return
    // Defense in depth: never render a link for a username outside the safe character set, even if one somehow
    // reached this far without going through `fromString`.
    if (!VALID_USERNAME_REGEX.test(username)) return

    // Build the anchor with DOM APIs so the username and display name are inserted as a URL, attribute values,
    // and text — never parsed as HTML. This prevents DOM XSS from attacker-controlled display names/usernames.
    const anchor = document.createElement('a')
    anchor.href = `https://${window.location.hostname}/${encodeURIComponent(username)}`
    anchor.target = '_blank'
    anchor.rel = 'noopener noreferrer'
    anchor.dataset.bridgedLink = 'profile'
    anchor.dataset.bridgedLinkUsername = username
    anchor.textContent = this.mentioneeDisplayName ?? __('Someone')
    this.replaceChildren(anchor)
  }
}

export { MENTION_ELEMENT_TAG_NAME, PdltMention, type MentionType }
